import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeColors } from '../../utils/theme';
import API_ENDPOINTS from '../../config';
import { authenticatedFetch } from '../../utils/api';
import logger from '../../utils/logger';

export default function AdminCompanies() {
  const { t, i18n } = useTranslation();
  const { isDark } = useTheme();
  const colors = getThemeColors(isDark);
  const [companies, setCompanies] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    trialStatus: '',
    paidStatus: ''
  });
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});

  const fetchCompanies = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...(searchTerm && { search: searchTerm }),
        ...(filters.trialStatus !== '' && { trialStatus: filters.trialStatus }),
        ...(filters.paidStatus !== '' && { paidStatus: filters.paidStatus })
      });

      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/companies?${params}`, {
        method: 'GET',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch companies');
      }

      const data = await response.json();
      setCompanies(data.companies || []);
      setPagination(data.pagination || {
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
      });
    } catch (error) {
      // Don't log redirect errors - user is being redirected to login
      if (!error.redirected && error.message !== 'Unauthorized - redirected to login') {
        logger.error('Error fetching companies:', error);
      }
    } finally {
      setLoading(false);
    }
  }, [searchTerm, filters]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setEditForm({
      name: company.name || '',
      pib: company.pib || '',
      mb: company.mb || '',
      address: company.address || '',
      city: company.city || '',
      postalCode: company.postalCode || '',
      phone: company.phone || '',
      email: company.email || '',
      website: company.website || '',
      trialStartDate: company.trialStartDate ? company.trialStartDate.split('T')[0] : '',
      trialEndDate: company.trialEndDate ? company.trialEndDate.split('T')[0] : '',
      isTrialActive: company.isTrialActive || false,
      isPaid: company.isPaid || false
    });
    setEditMode(true);
  };

  const handleUpdateCompany = async () => {
    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/companies/${selectedCompany.id}`, {
        method: 'PUT',
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update company');
      }

      // Refresh companies list
      fetchCompanies(pagination?.page || 1);
      setEditMode(false);
      setSelectedCompany(null);
    } catch (error) {
      logger.error('Error updating company:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(error.message);
      }
    }
  };

  const handleDeleteCompany = async (companyId) => {
    if (!window.confirm(t('admin.companies.deleteConfirm'))) {
      return;
    }

    try {
      const response = await authenticatedFetch(`${API_ENDPOINTS.API_BASE_URL}/api/admin/companies/${companyId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete company');
      }

      // Refresh companies list
      fetchCompanies(pagination?.page || 1);
    } catch (error) {
      logger.error('Error deleting company:', error);
      if (error.message !== 'Unauthorized - redirected to login') {
        alert(t('admin.companies.failedToLoad'));
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const localeMap = {
      'sr': 'sr-RS',
      'zh': 'zh-CN',
      'en': 'en-US'
    };
    const locale = localeMap[i18n.language] || 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading && companies.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div style={{ fontSize: '24px', marginBottom: '16px' }}>⏳</div>
        <div style={{ fontSize: '18px', color: colors.textSecondary }}>{t('admin.companies.loading')}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 700, color: colors.text, margin: '0 0 0.5rem 0' }}>
          {t('admin.companies.title')}
        </h1>
        <p style={{ color: colors.textSecondary, margin: 0 }}>
          {t('admin.companies.subtitle')}
        </p>
      </div>

      {/* Search and Filters */}
      <div style={{
        background: colors.card,
        padding: '1.5rem',
        borderRadius: '12px',
        boxShadow: colors.shadow,
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.companies.searchLabel')}
            </label>
            <input
              type="text"
              placeholder={t('admin.companies.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.companies.filterByTrial')}
            </label>
            <select
              value={filters.trialStatus}
              onChange={(e) => setFilters({ ...filters, trialStatus: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            >
              <option value="">{t('admin.companies.allCompanies')}</option>
              <option value="true">{t('admin.companies.activeTrial')}</option>
              <option value="false">{t('admin.companies.inactiveTrial')}</option>
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
              {t('admin.companies.filterByPaid')}
            </label>
            <select
              value={filters.paidStatus}
              onChange={(e) => setFilters({ ...filters, paidStatus: e.target.value })}
              style={{
                width: '100%',
                padding: '0.75rem',
                border: `1px solid ${colors.border}`,
                borderRadius: '6px',
                fontSize: '14px',
                background: colors.background,
                color: colors.text
              }}
            >
              <option value="">{t('admin.companies.allCompanies')}</option>
              <option value="true">{t('admin.companies.paidSubscriptions')}</option>
              <option value="false">{t('admin.companies.unpaidSubscriptions')}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Companies Table */}
      <div style={{
        background: colors.card,
        borderRadius: '12px',
        boxShadow: colors.shadow,
        overflow: 'hidden'
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: colors.backgroundLight }}>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.companies.company')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.companies.pibMb')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('common.status')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.companies.usersFiles')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'left', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.companies.trialPeriod')}
                </th>
                <th style={{ padding: '1rem', textAlign: 'center', fontSize: '14px', fontWeight: 600, color: colors.text }}>
                  {t('admin.companies.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {companies.map((company, index) => (
                <tr key={company.id} style={{ 
                  borderBottom: index < companies.length - 1 ? `1px solid ${colors.border}` : 'none',
                  background: index % 2 === 0 ? colors.card : colors.backgroundLight
                }}>
                  <td style={{ padding: '1rem' }}>
                    <div>
                      <div style={{ fontWeight: 500, color: colors.text, marginBottom: '4px' }}>
                        {company.name}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {company.email}
                      </div>
                      <div style={{ fontSize: '12px', color: colors.textSecondary }}>
                        {company.address}, {company.city}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      <div>{t('admin.companies.pib')}: {company.pib}</div>
                      <div>{t('admin.companies.mb')}: {company.mb}</div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: company.isTrialActive ? '#fef3c7' : '#d1fae5',
                        color: company.isTrialActive ? '#d97706' : '#059669'
                      }}>
                        {company.isTrialActive ? t('admin.companies.trialActive') : t('admin.companies.trialInactive')}
                      </span>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 500,
                        background: company.isPaid ? '#d1fae5' : '#fee2e2',
                        color: company.isPaid ? '#059669' : '#dc2626'
                      }}>
                        {company.isPaid ? t('admin.companies.paid') : t('admin.companies.unpaid')}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '14px', color: colors.text }}>
                      <div>👥 {t('admin.companies.usersCount', { count: company.userCount || 0 })}</div>
                      <div>📁 {t('admin.companies.filesCount', { count: company.uploadCount || 0 })}</div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', fontSize: '14px', color: colors.textSecondary }}>
                    <div>
                      <div>{t('admin.companies.from')}: {formatDate(company.trialStartDate)}</div>
                      <div>{t('admin.companies.to')}: {formatDate(company.trialEndDate)}</div>
                    </div>
                  </td>
                  <td style={{ padding: '1rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEditCompany(company)}
                        style={{
                          padding: '6px 12px',
                          background: colors.primary,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = colors.primaryHover}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.primary}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        onClick={() => handleDeleteCompany(company.id)}
                        style={{
                          padding: '6px 12px',
                          background: colors.error,
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          transition: 'background-color 0.2s'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = isDark ? '#ef4444' : '#b91c1c'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = colors.error}
                      >
                        {t('common.delete')}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination?.pages > 1 && (
          <div style={{ 
            padding: '1rem', 
            borderTop: `1px solid ${colors.border}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ fontSize: '14px', color: colors.textSecondary }}>
              {t('admin.companies.showingCompanies', {
                from: ((pagination.page - 1) * pagination.limit) + 1,
                to: Math.min(pagination.page * pagination.limit, pagination.total),
                total: pagination.total
              })}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => fetchCompanies(pagination.page - 1)}
                disabled={pagination.page <= 1}
                style={{
                  padding: '8px 12px',
                  background: pagination.page <= 1 ? colors.backgroundLight : colors.primary,
                  color: pagination.page <= 1 ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page <= 1 ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.previous')}
              </button>
              <span style={{ padding: '8px 12px', fontSize: '14px', color: colors.text }}>
                {t('admin.companies.pageOf', { page: pagination.page, pages: pagination.pages })}
              </span>
              <button
                onClick={() => fetchCompanies(pagination.page + 1)}
                disabled={pagination.page >= pagination.pages}
                style={{
                  padding: '8px 12px',
                  background: pagination.page >= pagination.pages ? colors.backgroundLight : colors.primary,
                  color: pagination.page >= pagination.pages ? colors.textLight : 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: pagination.page >= pagination.pages ? 'not-allowed' : 'pointer'
                }}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Edit Company Modal */}
      {editMode && selectedCompany && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: colors.card,
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.25rem', fontWeight: 600, color: colors.text }}>
              {t('admin.companies.editCompany')}
            </h3>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.companyName')}
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.pib')}
                </label>
                <input
                  type="text"
                  value={editForm.pib}
                  onChange={(e) => setEditForm({ ...editForm, pib: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.mb')}
                </label>
                <input
                  type="text"
                  value={editForm.mb}
                  onChange={(e) => setEditForm({ ...editForm, mb: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.email')}
                </label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.phone')}
                </label>
                <input
                  type="text"
                  value={editForm.phone}
                  onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.website')}
                </label>
                <input
                  type="text"
                  value={editForm.website}
                  onChange={(e) => setEditForm({ ...editForm, website: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.address')}
                </label>
                <input
                  type="text"
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.city')}
                </label>
                <input
                  type="text"
                  value={editForm.city}
                  onChange={(e) => setEditForm({ ...editForm, city: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.postalCode')}
                </label>
                <input
                  type="text"
                  value={editForm.postalCode}
                  onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.trialStartDate')}
                </label>
                <input
                  type="date"
                  value={editForm.trialStartDate}
                  onChange={(e) => setEditForm({ ...editForm, trialStartDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontSize: '14px', fontWeight: 500, color: colors.text }}>
                  {t('admin.companies.trialEndDate')}
                </label>
                <input
                  type="date"
                  value={editForm.trialEndDate}
                  onChange={(e) => setEditForm({ ...editForm, trialEndDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: `1px solid ${colors.border}`,
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: colors.background,
                    color: colors.text
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="isTrialActive"
                  checked={editForm.isTrialActive}
                  onChange={(e) => setEditForm({ ...editForm, isTrialActive: e.target.checked })}
                />
                <label htmlFor="isTrialActive" style={{ fontSize: '14px', color: colors.text }}>
                  {t('admin.companies.isTrialActive')}
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="isPaid"
                  checked={editForm.isPaid}
                  onChange={(e) => setEditForm({ ...editForm, isPaid: e.target.checked })}
                />
                <label htmlFor="isPaid" style={{ fontSize: '14px', color: colors.text }}>
                  {t('admin.companies.paidSubscription')}
                </label>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '2rem' }}>
              <button
                onClick={handleUpdateCompany}
                style={{
                  padding: '12px 24px',
                  background: colors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('admin.companies.saveChanges')}
              </button>
              <button
                onClick={() => {
                  setEditMode(false);
                  setSelectedCompany(null);
                }}
                style={{
                  padding: '12px 24px',
                  background: colors.backgroundLight,
                  color: colors.text,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  flex: 1
                }}
              >
                {t('admin.companies.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 